// Small pure facts about a patient record, shared by the list and the workspace.
//
// Everything here is defensive about free-text service columns: gender and
// birth_date have no validator server-side (CORE.md §11 G11), so the client
// renders what it recognises and passes the rest through UNCHANGED rather than
// normalising a value it does not understand into a wrong one.

/**
 * The catalogue key for a gender value, or null when the stored string is not
 * one this app has copy for — in which case the RAW value is rendered. A
 * patient whose record says «آقا» must not be re-labelled.
 *
 * i18n-exempt: the strings below are STORED VALUES being recognised, not copy
 * being shown. The label the dentist reads is t('common.gender.male').
 */
export function genderKey(raw) {
  const s = String(raw || '').toLowerCase().trim();
  if (['male', 'm', 'مرد', 'آقا'].includes(s)) return 'common.gender.male';
  if (['female', 'f', 'زن', 'خانم'].includes(s)) return 'common.gender.female';
  return null;
}

/**
 * The catalogue key for a marital-status value, or null when the stored string
 * is not one this app has copy for — in which case the RAW value renders, for
 * the same reason gender does: the column is free text with no validator, and a
 * record that says «متأهل» must not be re-labelled.
 *
 * i18n-exempt: the strings below are STORED VALUES being recognised, not copy
 * being shown. The label the dentist reads is t('common.marital.single').
 */
export function maritalStatusKey(raw) {
  const s = String(raw || '').toLowerCase().trim();
  if (['single', 'unmarried', 'مجرد'].includes(s)) return 'common.marital.single';
  if (['married', 'متاهل', 'متأهل'].includes(s)) return 'common.marital.married';
  if (['divorced', 'مطلقه'].includes(s)) return 'common.marital.divorced';
  if (['widowed', 'widow', 'بیوه'].includes(s)) return 'common.marital.widowed';
  return null;
}

/**
 * The catalogue key for a recorded allergy severity, or null when the stored
 * value is not one this app has copy for — in which case the RAW value renders.
 *
 * Deliberately NOT folded onto the نسخه sweep's high/moderate/low scale: that
 * mapper defaults an unrecognised value to 'low', which is a safe default for a
 * drug-interaction hint and a dangerous one for a recorded allergy. Here an
 * unknown value is shown verbatim rather than relabelled downward.
 *
 * i18n-exempt: the strings below are STORED VALUES being recognised, not copy.
 */
export function allergySeverityKey(raw) {
  const s = String(raw || '').toLowerCase().trim();
  if (['severe', 'high', 'critical'].includes(s)) return 'patient.severity.severe';
  if (['moderate', 'medium'].includes(s)) return 'patient.severity.moderate';
  if (['mild', 'low'].includes(s)) return 'patient.severity.mild';
  return null;
}

/**
 * The catalogue key for a condition's FHIR clinical status, or null → render
 * the raw value. Same rule as above: recognise, never rewrite.
 */
export function conditionStatusKey(raw) {
  const s = String(raw || '').toLowerCase().trim();
  if (s === 'active') return 'patient.conditionStatus.active';
  if (s === 'inactive') return 'patient.conditionStatus.inactive';
  if (s === 'resolved') return 'patient.conditionStatus.resolved';
  if (s === 'remission') return 'patient.conditionStatus.remission';
  return null;
}

/** A search term worth sending. One character matches most of a register. */
export const MIN_SEARCH_LENGTH = 2;

export function isSearchable(term) {
  return String(term || '').trim().length >= MIN_SEARCH_LENGTH;
}

/** The term as the wire should see it: trimmed, or null when it is not a search. */
export function searchTerm(term) {
  const s = String(term || '').trim();
  return s.length >= MIN_SEARCH_LENGTH ? s : null;
}

/**
 * Does this membership authorize reading the clinic's patients?
 *
 * MIRRORS `authorizesPatients` in parsina.bff.dentist/src/auth/patientScope.js.
 * The BFF filters every membership through the same two explicit nos before a
 * clinic id may scope anything, and refuses a clinicId it does not count with
 * CLINIC_FORBIDDEN. The two copies MUST agree: scoping on a membership the
 * server rejects turns «در انتظار تأیید» into «شما عضو این مطب
 * نیستید» under a red error card, on a screen the dentist cannot fix.
 *
 * `joinClinicByCode` writes the row with status 'pending'
 * (doctorpreference/app/adapters/postgres_repository.py:328); a separate
 * AcceptClinicDoctor sets 'accepted' (ibid.:389-395). `active === false` is
 * defensive only — `list_doctor_clinics` already filters it server-side — but
 * keeping it here keeps this predicate the same shape as the BFF's.
 *
 * Only the two EXPLICIT nos are excluded, exactly as server-side: a legacy row
 * carrying no status at all is not evidence of a refusal, and locking a working
 * dentist out of their own register over a NULL is the worse failure.
 */
export function authorizesPatients(link) {
  return link?.active !== false && link?.status !== 'pending';
}

/**
 * Which clinic a query should be scoped to.
 *
 * The BFF refuses an ambiguous request (CLINIC_REQUIRED) rather than guessing,
 * and returns an EMPTY page — never the platform's whole registry — for a
 * dentist with no membership. This mirrors that decision client-side so the
 * screen can render the right empty state instead of firing a call it knows
 * will fail:
 *
 *   'none'     no membership at all         → the "join a clinic" empty state
 *   'pending'  memberships, none accepted   → the "waiting to be accepted" state
 *   'ready'    one usable, or the dentist picked one → send `clinicId`
 *
 * Only an AUTHORIZED membership may scope a query. `myClinics` deliberately
 * lists pending ones so پروفایل can show «در انتظار تأیید», which is
 * exactly why this cannot take list[0]: `list_doctor_clinics` has no ORDER BY,
 * so a dentist with one accepted and one pending clinic got whichever row the
 * database happened to return first — and on the pending one every clinic-scoped
 * screen failed.
 *
 * 'pending' is its own state and not a variety of 'none': the dentist HAS asked
 * to join and the answer is with the clinic, so «هنوز عضو هیچ کلینیکی
 * نیستید» would send them to re-enter a code they already used.
 */
export function clinicScopeState({ clinics, activeId }) {
  const list = clinics || [];
  const usable = list.filter(authorizesPatients);
  if (usable.length === 0) {
    return { state: list.length > 0 ? 'pending' : 'none', clinicId: null };
  }
  if (activeId && usable.some((c) => c.clinicId === activeId)) {
    return { state: 'ready', clinicId: activeId };
  }
  return { state: 'ready', clinicId: usable[0].clinicId };
}
